import React from 'react'
import './layout.scss'
import { RichTextRenderer } from '../RichTextRenderer'
import { PortableTextBlock } from '@portabletext/react'
import { H6 } from '../Heading'

interface LayoutProps {
  images: string[]
  layoutColumns: number
  dataTestId: string
  splitMiddleColumn?: boolean
  imageClasses?: string
  imageContainerClasses?: string
  layoutContainerClasses?: string
  /** Applied to each image cell wrapper (e.g. when using `card` classes). */
  imageCellBackgroundColor?: string
}

const ImageGridLayout = ({
  images,
  layoutColumns,
  dataTestId,
  splitMiddleColumn,
  imageClasses,
  imageContainerClasses,
  layoutContainerClasses = 'mt-64px',
  imageCellBackgroundColor,
}: LayoutProps) => {
  if (!images?.length) {
    return <></>
  }

  // Special case for 4 images 3 columns
  if (images?.length === 4 && layoutColumns === 3 && splitMiddleColumn) {
    return (
      <div className={`${layoutContainerClasses} flex gap-10px`} data-testid={dataTestId}>
        <div className='image-grid grid-3-split-middle'>
          {images?.map((url: string, idx: number) => (
            <div
              key={idx}
              className={`img-${idx + 1} ${imageContainerClasses}`}
              style={
                imageCellBackgroundColor ? { backgroundColor: imageCellBackgroundColor } : undefined
              }
            >
              <img
                src={url}
                alt={`Image ${idx + 1}`}
                className={`width-100percent height-100percent ${imageClasses}`}
              />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Regular layouts based on layoutColumns
  return (
    <div className={`${layoutContainerClasses} flex gap-10px`} data-testid={dataTestId}>
      <div className={`image-grid grid-${layoutColumns}`}>
        {images?.map((url: string, idx: number) => (
          <div
            key={idx}
            className={imageContainerClasses}
            style={
              imageCellBackgroundColor ? { backgroundColor: imageCellBackgroundColor } : undefined
            }
          >
            <img
              src={url}
              alt={`Image ${idx + 1}`}
              className={`width-100percent height-100percent ${imageClasses}`}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const CardGridLayout = ({
  cardItems,
  images,
  layoutColumns,
  dataTestId,
  layoutContainerClasses = 'mt-24px',
  textContentClassName = '',
}: {
  cardItems?: { primaryText?: string; blockText: PortableTextBlock[] }[]
  images?: string[]
  layoutColumns: number
  dataTestId: string
  layoutContainerClasses?: string
  textContentClassName?: string
}) => {
  const textContentClasses = ['card-padding', textContentClassName].filter(Boolean).join(' ')

  return (
    <div className={`${layoutContainerClasses} flex gap-10px`} data-testid={dataTestId}>
      <div className={`image-grid image-grid--stretch-cards grid-${layoutColumns}`}>
        {cardItems?.map((card, idx: number) => (
          <div className='card' key={idx}>
            <div className={textContentClasses}>
              {card.primaryText && (
                <H6 text={<strong>{card.primaryText}</strong>} classes={['mb-12']} />
              )}
              <RichTextRenderer content={card.blockText} variant='opportunity-details' />
            </div>
          </div>
        ))}
        {images?.map((url: string, idx: number) => (
          <div className='card card-image' key={idx}>
            <img
              src={url}
              alt={`Image ${idx + 1}`}
              className='height-100percent width-100percent'
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export { ImageGridLayout, CardGridLayout }
